import { useState } from 'react';
import { Card, Form, Button, Select, Statistic, Col, Typography, Table, Tag } from 'antd';
import { useParams } from 'react-router-dom';
import { companies } from '../../../../../../../API/TT_ELD/Users/company';
import { useCompanyData } from '../../../../../../../Hooks/TT_ELD/Companies';
import { useHistories } from '../../../../../../../Hooks/TT_ELD/Team/Histories';
import moment from 'moment';

const { Timer } = Statistic;
const { Text } = Typography;

type MyObjectType = { [key: string | number]: any };
type params = { readonly id: any };

const DURATION_OPTIONS = [
  { value: 30, label: '30 seconds' },
  { value: 1 * 24 * 60 * 60, label: '1 day' },
  { value: 2 * 24 * 60 * 60, label: '2 days' },
  { value: 3 * 24 * 60 * 60, label: '3 days' },
];

const HISTORY_STATUS = 'edit-access-updated';
const HISTORY_PAGE_SIZE = 10;

const parseHistoryText = (text?: string) => {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
};

// Backend sends 'N/A' (or nothing) when a side of the change had no value
const formatExpiresAt = (value: any) =>
  value && value !== 'N/A' && moment(value).isValid() ? moment(value).format('YYYY-MM-DD HH:mm') : 'N/A';

const formatDays = (value: any) =>
  value === null || value === undefined || value === 'N/A'
    ? 'N/A'
    : `${value} day${Number(value) === 1 ? '' : 's'}`;

const historyColumns = [
  {
    title: 'No',
    dataIndex: 'no',
    key: 'no',
    width: 60,
  },
  {
    title: 'Updated Date',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (date?: string) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-'),
  },
  {
    title: 'Expires At (From → To)',
    dataIndex: 'editExpiresAt',
    key: 'editExpiresAt',
    render: (change: any) => (
      <span>
        {formatExpiresAt(change?.from)} → {formatExpiresAt(change?.to)}
      </span>
    ),
  },
  {
    title: 'Remaining Days (From → To)',
    dataIndex: 'days',
    key: 'days',
    render: (change: any) => (
      <span>
        {formatDays(change?.from)} → {formatDays(change?.to)}
      </span>
    ),
  },
  {
    title: 'Updated By',
    dataIndex: 'editedBy',
    key: 'editedBy',
    render: (editedBy: any) => {
      if (!editedBy) return '-';
      const fullName = [editedBy.first_name, editedBy.second_name].filter(Boolean).join(' ');
      return (
        <div>
          <div>{fullName || '-'}</div>
          {editedBy.email && <Text type="secondary">{editedBy.email}</Text>}
        </div>
      );
    },
  },
  {
    title: 'Role',
    dataIndex: 'editedBy',
    key: 'role',
    render: (editedBy: any) => (editedBy?.admin_type ? <Tag>{editedBy.admin_type}</Tag> : '-'),
  },
];

const LogEditAccess = () => {
  const [form] = Form.useForm();
  const { id } = useParams<params>();
  const { data, isFetching, isLoading, refetch }: MyObjectType = useCompanyData(id);

  const editExpiresAt = data?.config?.editExpiresAt;
  const editExpiresAtDate = editExpiresAt ? new Date(editExpiresAt) : undefined;
  const isAccessActive = !!editExpiresAtDate && editExpiresAtDate.getTime() > Date.now();

  const [historyPage, setHistoryPage] = useState(1);
  const history: MyObjectType = useHistories('', '', id, historyPage, '', HISTORY_STATUS);
  const historyDataSource = history.data?.data?.map((row: any, index: number) => {
    const changes = parseHistoryText(row.text);
    return {
      key: row.id ?? index,
      no: (historyPage - 1) * HISTORY_PAGE_SIZE + index + 1,
      createdAt: row.createdAt,
      editExpiresAt: changes.editExpiresAt,
      days: changes.days,
      editedBy: row.editedBy,
    };
  });

  const onFinish = async (values: { duration: number }) => {
    const isoString = moment().add(values.duration, 'seconds').toISOString();
    await companies.companyPatchData(
      { is_active: true, config: { editExpiresAt: isoString } },
      id,
    );
    refetch();
    history.refetch();
  };

  return (
    <div className="mainPage">
      <Card loading={isLoading || isFetching} title="Log Edit Access" style={{ width: '100%' }}>
        {isAccessActive && editExpiresAtDate && (
          <Col style={{ marginBottom: 32 }}>
            <Timer
              type="countdown"
              valueStyle={{ fontSize: '32px' }}
              title="Log edit access ends"
              value={editExpiresAtDate.getTime()}
              format="DD : HH : mm : ss"
            />
            <Text type="secondary">
              Active until {moment(editExpiresAtDate).format('YYYY-MM-DD HH:mm')}
            </Text>
          </Col>
        )}

        {!isAccessActive && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            No active log edit access. Select a duration below to grant temporary access.
          </Text>
        )}

        <Form
          form={form}
          layout="vertical"
          style={{ maxWidth: 400 }}
          onFinish={onFinish}
          name="log_edit_access_form"
          initialValues={{ duration: DURATION_OPTIONS[1].value }}
        >
          <Form.Item
            label="Log Edit Access Duration"
            name="duration"
            rules={[{ required: true, message: 'Please select a duration!' }]}
          >
            <Select style={{ width: '100%' }}>
              {DURATION_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Log edit access history" style={{ width: '100%', marginTop: 16 }}>
        <Table
          dataSource={historyDataSource}
          columns={historyColumns}
          loading={history.isLoading || history.isFetching}
          scroll={{ x: 'max-content' }}
          pagination={{
            total: history.data?.count,
            current: historyPage,
            pageSize: HISTORY_PAGE_SIZE,
            showSizeChanger: false,
            onChange: setHistoryPage,
          }}
        />
      </Card>
    </div>
  );
};

export default LogEditAccess;
